import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useAuth } from "../contexts/AuthContext";
import SuperAdminSubscriptionDashboard from "../subscription/SuperAdminSubscriptionDashboard";
import Sidebar from "../navigations/Sidebar";
import AccessDenied from "../auth/AccessDenied";
import AuthenticatorGate from "../auth/AuthenticatorGate";
import Login from "../auth/Login";
import Dashboard from "../dashboard/Dashboard";
import StoresManagement from "../stores/StoresManagement";
import StripeSettings from "../settings/StripeSettings";
import LogoSettings from "../settings/LogoSettings";
import EatPrinterSettings from "../settings/EatPrinterSettings";
import PlanManagement from "../settings/PlanManagement";
import EmailSettings from "../settings/EmailSettings";
import EmailTemplateManagement from "../settings/EmailTemplateManagement";
import PosReceiptEmailManagement from "../settings/PosReceiptEmailManagement";
import GoogleSettings from "../settings/GoogleSettings";
import CloudflareSettings from "../settings/CloudflareSettings";
import ServerCertificatePage from "../settings/ServerCertificatePage";
import PaymentProcessorSettings from "../settings/PaymentProcessorSettings";
import APIMappingTool from "../settings/APIMappingTool";
import LivroNuageSettings from "../settings/LivroNuageSettings";
import UserSettings from "../settings/UserSettings";
import SystemLogPage from "../settings/SystemLogPage";
import ProfilePage from "../profile/ProfilePage";
import ForbiddenSubdomainSettings from "../settings/ForbiddenSubdomainSettings";
import TaxSettings from "../settings/TaxSettings";
import ColorPaletteSettings from "../settings/ColorPaletteSettings";
import PosWindowSettings from "../settings/PosWindowSettings";
import PermanentTimeSettings from "../settings/PermanentTimeSettings";
import ProductVersionsPage from "../settings/ProductVersionsPage";
import StorePage from "../stores/StorePage";
import NoticeBroadcast from "../notices/NoticeBroadcast";
import PosInformationPage from "../settings/PosInformationPage";
import { StationApprovalProvider } from "../stores/StationApprovalAlerts";
import PageShell from "../layout/PageShell";

const Finances = () => (
  <PageShell title="Finances" description="Billing summaries will be added here.">
    <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={6}>
      <Text fontWeight="600" mb={1}>
        No finance reports yet
      </Text>
      <Text fontSize="sm" color="gray.600">
        Billing summaries for stores will appear here.
      </Text>
    </Box>
  </PageShell>
);

const SuperAdminRoute = () => {
  const { currentUser, isSuperAdmin, isAuthStateChanged, needsAuthenticator } = useAuth();

  // Render sidebar immediately with loading state - don't block on auth
  const isLoading = !isAuthStateChanged;
  const showLogin = isAuthStateChanged && !currentUser;
  const showAccessDenied = isAuthStateChanged && currentUser && !isSuperAdmin;

  // Show login page if not authenticated
  if (showLogin) {
    return <Login />;
  }

  if (isAuthStateChanged && needsAuthenticator) {
    return <AuthenticatorGate />;
  }

  // Show access denied if not super admin
  if (showAccessDenied) {
    return <AccessDenied />;
  }

  // Always render sidebar structure - show loading content inside if needed
  return (
    <StationApprovalProvider>
    <Sidebar isLoading={isLoading}>
      {isLoading ? (
        <Flex justify="center" align="center" height="100vh">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/restaurants" element={<StoresManagement />} />
          <Route path="/restaurants/:storeId" element={<StorePage />} />
          <Route path="/notices" element={<NoticeBroadcast />} />
          <Route path="/products" element={<ProductVersionsPage />} />
          <Route path="/subscriptions" element={<SuperAdminSubscriptionDashboard />} />
          <Route path="/plans" element={<PlanManagement />} />
          <Route path="/logo-settings" element={<LogoSettings />} />
          <Route path="/stripe-settings" element={<StripeSettings />} />
          <Route path="/eat-printer" element={<EatPrinterSettings />} />
          <Route path="/google-settings" element={<GoogleSettings />} />
          <Route path="/email-settings" element={<EmailSettings />} />
          <Route path="/cloudflare-settings" element={<CloudflareSettings />} />
          <Route path="/server-certificate" element={<ServerCertificatePage />} />
          <Route path="/forbidden-subdomains" element={<ForbiddenSubdomainSettings />} />
          <Route path="/taxes" element={<TaxSettings />} />
          <Route path="/color-palettes" element={<ColorPaletteSettings />} />
          <Route path="/pos-window" element={<PosWindowSettings />} />
          <Route path="/permanent-time" element={<PermanentTimeSettings />} />
          <Route path="/cloud-mev-worker" element={<Navigate to="/cloudflare-settings" replace />} />
          <Route path="/payment-processor-settings" element={<PaymentProcessorSettings />} />
          <Route path="/user-settings" element={<UserSettings />} />
          <Route path="/system-log" element={<SystemLogPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/api-mapping" element={<APIMappingTool />} />
          <Route path="/livro-nuage-settings" element={<LivroNuageSettings />} />
          <Route path="/email-templates" element={<EmailTemplateManagement />} />
          <Route path="/pos-receipt-email" element={<PosReceiptEmailManagement />} />
          <Route path="/pos-information" element={<PosInformationPage />} />
          <Route path="/finances" element={<Finances />} />
        </Routes>
      )}
    </Sidebar>
    </StationApprovalProvider>
  );
};

export default SuperAdminRoute;

